import React from 'react'
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { ReportViewModel } from '../../model/ReportViewModel';
import { chartColors } from './chartColors';

interface IProps {
    view:ReportViewModel
}

const formatPercent = (v:number) => (v > 0 ? '+' : '') + v.toFixed(2) + '%';

// rebased: each series is % change from its first point in the period
const getChartData = (data:any, startDate:Date):number[][] => {
    const points:number[][] = [];
    for (let i = 0; i < data.length; i++) {
        let jsDate = new Date(data[i].date.substring(0, 10));
        if (jsDate.getTime() >= startDate.getTime()) {
            points.push([jsDate.getTime(), data[i].close]);
        }
    }
    const base = points.length ? points[0][1] : 0;
    return points.map(([x, y]) => [x, base ? ((y - base) / base) * 100 : 0]);
}

const getChartSeries = (stock:any, lineData:number[][], i:number) => ({
    name: stock.ticker,
    type: 'line',
    data: lineData,
    showSymbol: false,
    color: chartColors[i],
    lineStyle: { width: i === 0 ? 4 : 1 },
    markLine: i === 0 ? { silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: 'silver', width: 2 }, data: [{ yAxis: 0 }] } : undefined,
});

// price-sensitive announcements plotted as dots on the stock's own line
const getAnnouncementSeries = (stock:any, lineData:number[][], startDate:Date, i:number) => {
    const valueAt = (ms:number) => {
        let y:number|undefined;
        for (const [x, v] of lineData) {
            if (x > ms) break;
            y = v;
        }
        return y;
    }
    const points:any[] = [];
    for (let announcement of stock.announcements||[]) {
        if (!announcement.priceSensitive) continue;
        let ms = new Date(announcement.date.substring(0, 10)).getTime();
        if (ms <= startDate.getTime()) continue;
        let existing = points.find(p => p.value[0] === ms);
        if (existing) {
            existing.text += '<br/>' + announcement.headline;
            continue;
        }
        const y = valueAt(ms);
        if (y === undefined) continue;
        points.push({ value: [ms, y], text: stock.ticker + " : " + announcement.headline });
    }
    return {
        name: `${stock.ticker} announcements`,
        type: 'scatter',
        data: points,
        symbolSize: 8,
        color: chartColors[i],
        z: 3,
    };
}

const PriceComparison:React.FC<IProps> = (props:IProps)=>{
    const { view } = props;
    let seriesOptions:any = [];
    
    const data = view.priceComparison.stocks;
    if(data[0].stockData.length){
        const startDate = new Date(data[0].stockData[0].date)

        for (let i = 0; i < data.length; i++) {
            const lineData = getChartData(data[i].stockData, startDate);
            seriesOptions.push(getChartSeries(data[i], lineData, i));
            seriesOptions.push(getAnnouncementSeries(data[i], lineData, startDate, i));
        }
    }

    const options = {
        title: { text: 'Price & Performance (Rebased)', left: 'center', textStyle: { fontSize: 16, fontWeight: 'normal' } },
        tooltip: {
            trigger: 'axis',
            formatter: (params:any[]) => {
                const lines = params.map(p => p.seriesType === 'scatter' ? p.data.text : `${p.marker}${p.seriesName}: ${formatPercent(p.value[1])}`);
                return [moment(params[0].value[0]).format('D MMM YYYY'), ...lines].join('<br/>');
            }
        },
        legend: { orient: 'vertical', left: 60, top: 40, data: data.map((s:any) => s.ticker), borderColor: '#ccc', borderWidth: 1, backgroundColor: '#fff' },
        grid: { left: 60, right: 60, top: 50, bottom: 40 },
        xAxis: { type: 'time', axisLabel: { formatter: '{d} {MMM}' } },
        yAxis: { type: 'value', axisLabel: { formatter: (v:number) => (v > 0 ? ' + ' : '') + v + '%' } },
        series: seriesOptions,
    }
    
    return <section>
        <h3>Price</h3>
        <p>Period: ({view.startDate} - {view.endDate})</p>
        <ReactECharts option={options} notMerge={true} style={{ height: 400 }} />
    </section>
}

export default PriceComparison;
